// The typed OUTPUT payloads of audit kinds whose row is a measurement, not a note —
// plan 102 slice 01, "record dispatch truth". `byok_keys.status` and
// `tasks.hold_reason` are overwritten in place, so without these rows a key's status
// history and a task's hold duration are simply not kept. Writers build the payload
// through the schema, so a malformed row fails at the seam it enters.
// Split beside `./kind`; re-exported from `../audit.ts`.

import { z } from 'zod';
// A leaf as well: `value-objects/ids` imports only zod.
import { ModelCallJobIdSchema, TaskIdSchema, WebhookSourceIdSchema } from '../../value-objects/ids';
// A LEAF import too, for the TDZ reason below: `llm-credential/failure` imports only zod, where the
// `../../value-objects/llm-credential` barrel reaches `../runner` and back here.
import { LlmKeyFailureReasonSchema } from '../../value-objects/llm-credential/failure';
// A leaf as well: `value-objects/model-call` imports only zod and the ids leaf.
import {
  MODEL_CALL_MACHINE_REASON,
  ModelCallClusterReasonSchema,
  ModelCallExecutor,
  ModelCallKindSchema,
  ModelCallPinSchema,
} from '../../value-objects/model-call';
// Leaves too: `gitee-forge` / `gitlab-forge` import only zod and `value-objects/ids`
// (plus type-only imports).
// A leaf as well: `value-objects/prompt-audit` imports only zod and `node:crypto`.
import { PromptAuditSchema } from '../../value-objects/prompt-audit';
// LEAF imports, not the `../blocker` / `../byok` / `../task` barrels, on purpose: those
// barrels reach modules that import the audit vocabulary back, and entering the graph here
// through a barrel is a TDZ cycle (`Cannot access 'PromptRole' before initialization`,
// reproduced). Each leaf imports only zod.
import { BlockerGuidanceNoneReasonSchema } from '../blocker/guidance';
import { BYOKStatusSchema } from '../byok/vocabulary';
import { GiteeRepoPathSchema } from '../gitee-forge';
import { GitlabInstanceSchema, GitlabProjectPathSchema } from '../gitlab-forge';
import { TaskHoldReasonSchema } from '../task/hold';

/** An ISO-8601 instant with an offset — the one spelling a `*_at`/`until` field takes here. */
const IsoInstant = z.string().datetime({ offset: true });

/**
 * `billing.key.status_changed` — one row per `byok_keys.status` TRANSITION, written at
 * the caller seam that already audits the verify/cool/park/fail path (never inside the
 * query). `from: null` is the key's first status — the capture door writes it.
 * `from === to` is refused, because a no-op write moved nothing and a row claiming it
 * did would inflate every per-key transition count. `retry_at` is when the platform will look again (a cooled
 * key's reset, a transport failure's backoff), or `null` when nothing is scheduled.
 * Strict: key material can never ride along on the row.
 */
export const BillingKeyStatusChangedOutputSchema = z
  .object({
    key_id: z.string().min(1),
    from: BYOKStatusSchema.nullable(),
    to: BYOKStatusSchema,
    reason: z.string().min(1).nullable(),
    retry_at: IsoInstant.nullable(),
  })
  .strict()
  .refine((v) => v.from !== v.to, {
    message: 'billing.key.status_changed: from and to are the same status — not a transition',
    path: ['to'],
  });
export type BillingKeyStatusChangedOutput = z.infer<typeof BillingKeyStatusChangedOutputSchema>;

/**
 * `billing.key.priority_changed` (plan 102 slice 05b) — one row per `byok_keys.priority`
 * MOVE made through a product door (the org and personal REST routes, the MCP
 * `key_priority_set` actions), written by `apps/api`'s one flow beside the write.
 * Before this, the only way to reorder a pool was a direct DB write, which left no
 * row. `from === to` is refused for the reason `billing.key.status_changed` refuses
 * it: a no-op moved nothing. The bounds are not re-checked here — a row records what
 * the column held, and a pre-range stored value is still a fact. Strict: nothing but
 * the move rides the row.
 */
export const BillingKeyPriorityChangedOutputSchema = z
  .object({
    key_id: z.string().min(1),
    from: z.number().int(),
    to: z.number().int(),
  })
  .strict()
  .refine((v) => v.from !== v.to, {
    message: 'billing.key.priority_changed: from and to are the same priority — not a move',
    path: ['to'],
  });
export type BillingKeyPriorityChangedOutput = z.infer<typeof BillingKeyPriorityChangedOutputSchema>;

/**
 * `billing.key.capacity_changed` (capacity-aware scheduling) — one row per
 * `byok_keys.capacity` MOVE made through a product door (the org and personal REST
 * routes, the MCP `key_capacity_set` actions), written by `apps/api`'s one flow
 * beside the write. `null` is a real end: an undeclared key (the default for its
 * kind applies), so `from: null` is a first declaration and `to: null` a clear.
 * `from === to` is refused like the priority move: nothing moved. The bounds are not
 * re-checked here, for the priority row's reason. Strict: nothing but the move.
 */
export const BillingKeyCapacityChangedOutputSchema = z
  .object({
    key_id: z.string().min(1),
    from: z.number().int().nullable(),
    to: z.number().int().nullable(),
  })
  .strict()
  .refine((v) => v.from !== v.to, {
    message: 'billing.key.capacity_changed: from and to are the same capacity — not a move',
    path: ['to'],
  });
export type BillingKeyCapacityChangedOutput = z.infer<typeof BillingKeyCapacityChangedOutputSchema>;

/**
 * `billing.key.label_changed` (#5203) — an ORG key's `byok_keys.label` MOVED through
 * `PATCH /v1/billing/keys/:id/label`, the handle a route pins it by
 * (`llm_routes.key_label`). `null` is a real end: an unnamed key, so `from: null` is a
 * first name and `to: null` a clear. `from === to` is refused like the priority and
 * capacity moves: nothing moved, so the route writes no row. The pins the rename
 * carried are NOT on this row — each is its own `llm_route.set`. Strict: nothing but
 * the move.
 */
export const BillingKeyLabelChangedOutputSchema = z
  .object({
    key_id: z.string().min(1),
    from: z.string().min(1).max(64).nullable(),
    to: z.string().min(1).max(64).nullable(),
  })
  .strict()
  .refine((v) => v.from !== v.to, {
    message: 'billing.key.label_changed: from and to are the same label — not a move',
    path: ['to'],
  });
export type BillingKeyLabelChangedOutput = z.infer<typeof BillingKeyLabelChangedOutputSchema>;

/**
 * `task.held` — a hold was STAMPED on a pending task, by one of two writer families:
 * a dispatcher pass's `mark*Holds` (`@developerz/db` `queries/tasks/claim.ts`), or the
 * failure re-pend's windowed hold (`failOrRetryTask` via `taskFailureRependHold`). `until` is the window's end for a windowed reason (`hold_until`), or `null` for
 * a hold that lifts on a signal. Paired with the release it makes hold duration one
 * query over `audit_events` instead of an unrecorded overwrite.
 *
 * `key_ids` and `bound` ride a park the lane's Agents-page SELECTION caused (an explicit
 * purpose row in `llm_routes`): the `byok_keys` ids the task waits on, and `bound:
 * 'route'` saying only those were considered — so "why did this review wait while other
 * keys were idle" is a query. Absent on every other hold.
 *
 * `from` and `to` ride the ONE overwrite the audit history names as a transition:
 * the stale-key → `area_busy` restamp (`markAreaHolds`), where the row was parked on
 * a key whose reasoning the pass's area verdict superseded. Both fields appear
 * only on that named transition: a first stamp omits them, and the round trip's
 * return half (key admission re-parking the freed row onto `key_saturated`) is a
 * normal key park, reported like one.
 */
export const TaskHeldOutputSchema = z
  .object({
    task_id: z.string().min(1),
    reason: TaskHoldReasonSchema,
    until: IsoInstant.nullable(),
    key_ids: z.array(z.string().min(1)).min(1).optional(),
    bound: z.literal('route').optional(),
    from: TaskHoldReasonSchema.nullable().optional(),
    to: TaskHoldReasonSchema.optional(),
  })
  .strict();
export type TaskHeldOutput = z.infer<typeof TaskHeldOutputSchema>;

/**
 * `llm.failover` (plan 102 slice 06a) — a running session's pool key failed mid-run
 * and the session CONTINUED on the next pool credential. `from_key` is the key that
 * failed, `to_key` the one the session moved to (both `byok_keys` ids), `reason` the
 * key-failure vocabulary the rest of the pool speaks (`LlmKeyFailureReasonSchema`),
 * and `reset_at` the provider's stated reset for `from_key`, or `null` when its
 * refusal named none. `from_key === to_key` is refused: a hop back onto the key that
 * just failed is the ping-pong the per-run skip set exists to prevent, and a row
 * claiming a move that did not happen would inflate every failover count. No
 * `session_id`: the row's own `session_id` column already says whose run it was.
 * Strict: key material can never ride along on the row.
 *
 * Two producers, one shape: the in-cluster `runSession` (06a) and, for a box run,
 * the mothership's `fleet.req` `llm_credential_next` responder (06b) when it hands
 * the box a replacement. `reason` is a WALL (`credits_exhausted`) or a NON-WALL hop
 * (`rate_limited` / `provider_unavailable` / `slow`, #6098, `isKeyWall`); either way
 * the row is written only when a key was actually served — "no sibling" writes none.
 */
export const LlmFailoverOutputSchema = z
  .object({
    from_key: z.string().min(1),
    to_key: z.string().min(1),
    reason: LlmKeyFailureReasonSchema,
    reset_at: IsoInstant.nullable(),
  })
  .strict()
  .refine((v) => v.from_key !== v.to_key, {
    message: 'llm.failover: from_key and to_key are the same key — not a failover',
    path: ['to_key'],
  });
export type LlmFailoverOutput = z.infer<typeof LlmFailoverOutputSchema>;

/**
 * `model_call.offered` (#6759, epic #6552) — one `model_call_jobs` row was OFFERED to the
 * account's org machines. `call_bytes` is the served prompt's UTF-8 size as the machine
 * receives it, `model_pin` the cluster's exact pick (the key the call is sealed to), and
 * `eligible_machines` how many idle machines could claim it when it was offered. The
 * prompt itself never rides the row: it stays in Postgres and is nulled at settle. Strict.
 * Reader-first: no writer this release (#6762).
 */
export const ModelCallOfferedOutputSchema = z
  .object({
    job_id: ModelCallJobIdSchema,
    task_id: TaskIdSchema,
    kind: ModelCallKindSchema,
    call_bytes: z.number().int().nonnegative(),
    model_pin: ModelCallPinSchema,
    eligible_machines: z.number().int().nonnegative(),
  })
  .strict();
export type ModelCallOfferedOutput = z.infer<typeof ModelCallOfferedOutputSchema>;

const ModelCallSettledBase = {
  job_id: ModelCallJobIdSchema,
  task_id: TaskIdSchema,
  kind: ModelCallKindSchema,
  duration_ms: z.number().int().nonnegative(),
};

/**
 * `model_call.settled` (#6759) — who ANSWERED one model call. A `machine` settle is always
 * `answered` and names the machine that answered; a `cluster` settle names why the cluster
 * ran it (`ModelCallClusterReasonSchema`), and `runner_id` is the machine that had claimed
 * it, or `null` when none had. One row per job, beside the caller's own verdict row
 * (`triage.verdict`, `janitor.action`). Strict. Reader-first: no writer this release (#6762).
 */
export const ModelCallSettledOutputSchema = z.discriminatedUnion('executor', [
  z
    .object({
      ...ModelCallSettledBase,
      executor: z.literal(ModelCallExecutor.Machine),
      reason: z.literal(MODEL_CALL_MACHINE_REASON),
      runner_id: z.string().min(1),
    })
    .strict(),
  z
    .object({
      ...ModelCallSettledBase,
      executor: z.literal(ModelCallExecutor.Cluster),
      reason: ModelCallClusterReasonSchema,
      runner_id: z.string().min(1).nullable(),
    })
    .strict(),
]);
export type ModelCallSettledOutput = z.infer<typeof ModelCallSettledOutputSchema>;

/**
 * `model_call.handed_back` (2026-11-01) — the caller's drain left one OPEN offer to its
 * resumed caller: unsettled, still claimable or box-held, under its own `deadline_at`
 * (ISO, the bound past which the dead-caller sweep settles it). Never a settle; the job's
 * one `model_call.settled` row follows from whoever settles it. Strict.
 */
export const ModelCallHandedBackOutputSchema = z
  .object({
    job_id: ModelCallJobIdSchema,
    task_id: TaskIdSchema,
    kind: ModelCallKindSchema,
    deadline_at: IsoInstant,
  })
  .strict();
export type ModelCallHandedBackOutput = z.infer<typeof ModelCallHandedBackOutputSchema>;

/**
 * `task.saved_branch_retired` (#6082) — the mothership DELETED a terminal task's saved branch
 * on the maintainer's repo: the task's own `dz/<slug>--<8hex>` head, or its pre-#6082 legacy
 * save `dz/wip/<task id>`, whose tip was the task's saved (`Dz-Wip-Task`) commit and which no
 * pull request used (`apps/worker/src/cron/saved-branch-sweep.ts`, rules in
 * `apps/worker/src/fleet/saved-branch-retire.ts`). One row per delete the platform PERFORMED —
 * a branch already gone writes none. `tip_sha` is the commit the branch held, so the deleted
 * work stays recoverable (`git push origin <tip_sha>:refs/heads/<branch>` while GitHub keeps
 * the object). `reason` is the terminal status that retired it; `task_failed` only once the
 * failure is past `FAILED_SAVED_BRANCH_RETAIN_DAYS` (`savedBranchRetireEligibility`,
 * `../task/saved-branch-retention.ts`; a redispatch resumes a younger save). Strict.
 */
export const SavedBranchRetireReasonSchema = z.enum([
  'task_cancelled',
  'task_merged',
  'task_failed',
]);
export type SavedBranchRetireReason = z.infer<typeof SavedBranchRetireReasonSchema>;

export const TaskSavedBranchRetiredOutputSchema = z
  .object({
    task_id: z.string().min(1),
    /** The task's own branch (one path component under `dz/`), or the legacy `dz/wip/<task id>`. */
    branch: z
      .string()
      .regex(
        /^dz\/(?:[^/]+|wip\/tsk_[0-9a-f]{32})$/,
        'a saved branch is dz/<slug>--<8hex> or the legacy dz/wip/<task id>',
      ),
    tip_sha: z.string().regex(/^[0-9a-f]{40}$/, 'a 40-hex commit sha'),
    reason: SavedBranchRetireReasonSchema,
  })
  .strict();
export type TaskSavedBranchRetiredOutput = z.infer<typeof TaskSavedBranchRetiredOutputSchema>;

/**
 * `schedule.refused` (#5363) — WHY a due scheduled checkup minted nothing, the row's
 * `input.kind`. ONE closed set: the hourly lane writes it (`apps/worker/src/cron/
 * schedules.ts` `ScheduleRefusal`, whose arms are typed against these members) and
 * the terminal renders one explaining line per member
 * (`entities/terminal/renderers/maintenance.ts`), so a raw enum never reaches a
 * maintainer. A member added here without its locale line renders the loud-miss
 * marker in `terminal-events.test.ts`.
 */
export const ScheduleRefusalKind = {
  PolicyUnknown: 'policy_unknown',
  RepoIsAnchor: 'repo_is_anchor',
  RepoNotEnabled: 'repo_not_enabled',
  NoScoutCapacity: 'no_scout_capacity',
  NoInstallation: 'no_installation',
  BriefMissing: 'brief_missing',
  BriefTooLarge: 'brief_too_large',
  /** The schedule's previous firing still has a task in motion (#6353): never stacked. */
  PreviousInFlight: 'previous_in_flight',
} as const;
export type ScheduleRefusalKind = (typeof ScheduleRefusalKind)[keyof typeof ScheduleRefusalKind];

/**
 * `forge.mr.observed`'s merge verdict, by NAME: the arms of `@developerz/gitlab`'s
 * `GitLabMergeStatusVerdict` (`classifyMergeRequestMergeStatus`) — restated, because
 * domain imports nothing internal and the gitlab package may not import domain. The
 * worker that maps one onto the other pins the mirror.
 */
export const ForgeMergeVerdictKindSchema = z.enum([
  'accepts',
  'refuses',
  'conflicting',
  'undetermined',
  'queued',
  'unrecognized',
]);
export type ForgeMergeVerdictKind = z.infer<typeof ForgeMergeVerdictKindSchema>;

/**
 * `forge.mr.observed`'s CI verdict: the head pipeline's graded checks
 * (`gradeGitLabChecks`, whose conclusions are `passing` / `failing` / `pending`) rolled
 * up by {@link rollupForgeCiVerdict}, plus `none` — no check exists to grade.
 */
export const ForgeCiVerdictSchema = z.enum(['passing', 'failing', 'pending', 'none']);
export type ForgeCiVerdict = z.infer<typeof ForgeCiVerdictSchema>;

/**
 * One verdict over graded checks: any failing check fails it, else any pending check
 * holds it pending, else it passes. An empty list is `none`, never `passing` — no CI
 * is not green CI.
 */
export function rollupForgeCiVerdict(
  conclusions: readonly ('passing' | 'failing' | 'pending')[],
): ForgeCiVerdict {
  if (conclusions.length === 0) return 'none';
  if (conclusions.includes('failing')) return 'failing';
  if (conclusions.includes('pending')) return 'pending';
  return 'passing';
}

/**
 * `forge.mr.observed` (plan 101 slice 07) — the OUTPUT of one re-read of a merge
 * request on an allowlisted instance (`GITLAB_INSTANCES`: gitlab.com, jihulab.com).
 * Strict, so neither the API token nor the delivery payload can ride along: the row
 * carries what was graded, and the raw delivery stays in `inbound_events`.
 */
export const ForgeMrObservedOutputSchema = z.strictObject({
  instance: GitlabInstanceSchema,
  projectId: z.number().int().positive(),
  /** `path_with_namespace` as the re-read returned it (current, not the connect-time copy). */
  path: GitlabProjectPathSchema,
  iid: z.number().int().positive(),
  /** The MR's head commit (40- or 64-hex); null when GitLab reports none (no source branch). */
  headSha: z
    .string()
    .regex(/^(?:[0-9a-f]{40}|[0-9a-f]{64})$/)
    .nullable(),
  mergeVerdict: z.strictObject({
    kind: ForgeMergeVerdictKindSchema,
    /** GitLab's `detailed_merge_status` value the verdict was graded from. */
    status: z.string().max(64),
  }),
  ciVerdict: ForgeCiVerdictSchema,
  approvals: z.strictObject({
    /** Approvals given (`approved_by` length). */
    given: z.number().int().nonnegative(),
    /** `approvals_required`; 0 where no rule applies. */
    required: z.number().int().nonnegative(),
    /** GitLab's own `approved` (edition-dependent; see `GitLabApprovals`). */
    approved: z.boolean(),
  }),
});
export type ForgeMrObservedOutput = z.infer<typeof ForgeMrObservedOutputSchema>;

/**
 * Why `forge.mr.observe_refused` did not observe: the GitLab observation lane's
 * refusals, closed. Every one is a fact a retry cannot change; a transient failure is
 * retried and never lands here. The worker maps its own refusal union onto this with a
 * `satisfies Record<…>`, so a new refusal there does not compile until it is named here.
 */
export const ForgeMrObserveRefusalReasonSchema = z.enum([
  /** The source is gone (disconnected since the hint was enqueued). */
  'source_not_found',
  /** The hint names a source of another kind: a producer bug. */
  'source_not_gitlab',
  /** The source's API-token vault entry is gone. */
  'api_token_missing',
  /** The API-token vault entry does not open under this plane's key ring. */
  'api_token_unreadable',
  /** The source is not `connected` (errored or paused). */
  'source_not_connected',
  /** The hint names a project the source no longer observes. */
  'project_mismatch',
  /** GitLab answered 401: the API token is invalid, expired or revoked. */
  'gitlab_unauthorized',
  /** GitLab answered 403: the token lacks `read_api` or access to the project. */
  'gitlab_forbidden',
  /** GitLab answered 404: the MR or the project is gone, or invisible to the token. */
  'gitlab_not_found',
]);
export type ForgeMrObserveRefusalReason = z.infer<typeof ForgeMrObserveRefusalReasonSchema>;

/**
 * `forge.mr.observe_refused` (plan 101 slice 07) — the OUTPUT of one hint the lane
 * refused. Ids and the reason only: strict, so the token, the decrypt error and the
 * delivery payload cannot ride along. `projectId` and `mrIid` are the HINT's (GitLab's
 * numbers, not a tenant id); a mismatched source-side project id stays in the log.
 */
export const ForgeMrObserveRefusedOutputSchema = z.strictObject({
  sourceId: WebhookSourceIdSchema,
  projectId: z.number().int().positive(),
  mrIid: z.number().int().positive(),
  reason: ForgeMrObserveRefusalReasonSchema,
});
export type ForgeMrObserveRefusedOutput = z.infer<typeof ForgeMrObserveRefusedOutputSchema>;

/** One side's review tally on a Gitee PR: how many of the named people accepted. */
const GiteeAcceptTallySchema = z
  .strictObject({
    accepted: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  })
  .refine((t) => t.accepted <= t.total, 'accepted cannot exceed total');

/**
 * `forge.pr.observed` (plan 101 slice 10b, D10) — the OUTPUT of one re-read of a gitee.com
 * pull request. Its own kind rather than a widened `forge.mr.observed`, whose strict
 * output is GitLab-shaped (`instance`, `iid`, approvals): widening it is a stored-shape
 * change to a shipped kind. `forge` is a literal so a second forge on this kind is a
 * deliberate schema change. Strict: neither the token nor the delivery payload rides
 * along. Gitee's v5 PR read has no `merge_status` (only the webhook does), so the merge
 * facts are the read's own `state` / `mergeable` / `draft`, recorded as read.
 */
export const ForgePrObservedOutputSchema = z.strictObject({
  forge: z.literal('gitee'),
  /** `owner/repo` as the re-read returned it (`base.repo.full_name`). */
  path: GiteeRepoPathSchema,
  repoId: z.number().int().positive(),
  /** The PR's per-repo number. */
  number: z.number().int().positive(),
  /** The PR's head commit (40-hex); null when Gitee reports none. */
  headSha: z
    .string()
    .regex(/^[0-9a-f]{40}$/)
    .nullable(),
  state: z.enum(['open', 'closed', 'merged']),
  /** Gitee's own `mergeable`: no conflict and its merge checks satisfied, as it computes it. */
  mergeable: z.boolean(),
  draft: z.boolean(),
  /** The head commit's check runs, graded and rolled up ({@link rollupForgeCiVerdict}). */
  ciVerdict: ForgeCiVerdictSchema,
  /** `assignees[]` (审查人员), `accept` counted. */
  reviewers: GiteeAcceptTallySchema,
  /** `testers[]` (测试人员), `accept` counted. */
  testers: GiteeAcceptTallySchema,
});
export type ForgePrObservedOutput = z.infer<typeof ForgePrObservedOutputSchema>;

/**
 * Why `forge.pr.observe_refused` did not observe: the Gitee observation lane's refusals,
 * closed. Every one is a fact a retry cannot change; a transient failure (5xx, network,
 * timeout, Gitee's 401-shaped rate limit, D7) is retried and never lands here. The worker
 * maps its own refusal union onto this with a `satisfies Record<…>`.
 *
 * `gitee_unauthorized`, `gitee_forbidden` and `gitee_repo_not_found` are CONNECTION-level
 * (`WEBHOOK_SOURCE_READ_FAILURES`: they mark the source's health); `gitee_not_found` is
 * one PR. The two 404s are told apart by Gitee's body message (`Not Found Project` vs
 * `Pull Request not found`, measured 2026-10-11).
 */
export const ForgePrObserveRefusalReasonSchema = z.enum([
  /** The source is gone (disconnected since the hint was enqueued). */
  'source_not_found',
  /** The hint names a source of another kind: a producer bug. */
  'source_not_gitee',
  /** The source's API-token vault entry is gone. */
  'api_token_missing',
  /** The API-token vault entry does not open under this plane's key ring. */
  'api_token_unreadable',
  /** The source is not `connected` (errored or paused). */
  'source_not_connected',
  /** The hint's repo id, or the PR read's `base.repo.id`, is not the source's repo. */
  'repo_mismatch',
  /** Gitee answered 401 (not its rate limit): the token is invalid, expired or revoked. */
  'gitee_unauthorized',
  /** Gitee answered 403: the token cannot read the repo. */
  'gitee_forbidden',
  /** Gitee answered 404 `Not Found Project`: the repo was renamed, deleted or hidden. */
  'gitee_repo_not_found',
  /** Gitee answered 404 `Pull Request not found`: this PR is gone or invisible. */
  'gitee_not_found',
]);
export type ForgePrObserveRefusalReason = z.infer<typeof ForgePrObserveRefusalReasonSchema>;

/**
 * `forge.pr.observe_refused` (plan 101 slice 10b) — the OUTPUT of one hint the Gitee lane
 * refused. Ids and the reason only, strict. `repoId` and `number` are the HINT's.
 */
export const ForgePrObserveRefusedOutputSchema = z.strictObject({
  forge: z.literal('gitee'),
  sourceId: WebhookSourceIdSchema,
  repoId: z.number().int().positive(),
  number: z.number().int().positive(),
  reason: ForgePrObserveRefusalReasonSchema,
});
export type ForgePrObserveRefusedOutput = z.infer<typeof ForgePrObserveRefusedOutputSchema>;

/**
 * The `last_error` values ingest's DELIVERY health writes (#6167): its `SourceFailure`
 * classes (`apps/ingest/src/sources.ts`, typed off this set, so a new failure arm there
 * does not compile until it is named here). Restated because domain imports no app. A
 * stored one-way door like `WEBHOOK_SOURCE_READ_FAILURES`: never renamed, only added.
 */
export const WebhookSourceDeliveryFailureSchema = z.enum([
  'bad_signature',
  'secret_unavailable',
  'nonce_claim_failed',
  'persist_failed',
  'mapping_failed',
  'handoff_failed',
  'claim_unsecured',
  'intake_cap_check_failed',
  'ask_generation_read_failed',
  // A Sentry delivery naming a project no route owns (owner ruling 2026-11-03): dropped, never
  // filed on the pin. Not the secret's fault: it marks the connection so the maintainer sees
  // which connection needs a `projects` route; the next routed delivery clears it.
  'unrouted_project',
]);
export type WebhookSourceDeliveryFailure = z.infer<typeof WebhookSourceDeliveryFailureSchema>;

/**
 * The read half of the stored `last_error` set, `WEBHOOK_SOURCE_READ_FAILURES` RESTATED
 * (the TDZ cycle {@link WebhookSourceHealthSnapshotSchema} names): the GitLab read's
 * four, then the Gitee read's three (slice 10b). Two flat enums, never a nested union, so
 * the snapshot's `lastError` stays a union of enums `audit-payload.test.ts` can list.
 */
const GitlabReadFailureMirrorSchema = ForgeMrObserveRefusalReasonSchema.extract([
  'gitlab_unauthorized',
  'gitlab_forbidden',
  'api_token_unreadable',
  'api_token_missing',
]);
const GiteeReadFailureMirrorSchema = ForgePrObserveRefusalReasonSchema.extract([
  'gitee_unauthorized',
  'gitee_forbidden',
  'gitee_repo_not_found',
]);

/** Every `last_error` value a health writer stores today: the delivery and the read failures. */
const WebhookSourceKnownLastErrorSchema = z.union([
  WebhookSourceDeliveryFailureSchema,
  GitlabReadFailureMirrorSchema,
  GiteeReadFailureMirrorSchema,
]);

/**
 * One side of a `webhook_source.health_changed` row: the source's `status` / `last_error`.
 * Both sets are RESTATED, not imported, because `../webhook-source` imports this
 * directory's barrel (the TDZ cycle the header names): `status` is
 * `WebhookSourceStatusSchema`'s members, and the read half of `lastError` is
 * `WEBHOOK_SOURCE_READ_FAILURES`. `audit-payload.test.ts` pins both mirrors.
 *
 * `unrecognized` (#6170) is the one member outside those sets: the column is
 * unconstrained `text`, so a row can still hold a value no writer stores today (one
 * written before the set was closed). {@link webhookSourceHealthTransition} records any
 * such value as `unrecognized`, so the transition is audited without the row naming a
 * value this schema has not closed over. Any other string is still refused.
 */
export const WebhookSourceHealthSnapshotSchema = z.strictObject({
  status: z.enum(['connected', 'error']),
  lastError: z
    .union([
      WebhookSourceDeliveryFailureSchema,
      GitlabReadFailureMirrorSchema,
      GiteeReadFailureMirrorSchema,
      z.enum(['unrecognized']),
    ])
    .nullable(),
});
export type WebhookSourceHealthSnapshot = z.infer<typeof WebhookSourceHealthSnapshotSchema>;

/**
 * Which health writer moved the row: ingest's delivery tracker (`ingest`), the worker's
 * forge read lanes (`worker`: `gitlab.mr_hint`, `gitee.pr_hint`), or a signing-secret
 * rotation in the api (`rotation`, #6180), which resets every `last_error` but a read
 * failure. A stored one-way door like the `last_error` sets: never renamed, only added.
 * `rotation` names the ACT, not the app, so a later api writer of another kind gets its
 * own value rather than sharing one.
 */
export const WebhookSourceHealthWriterSchema = z.enum(['ingest', 'worker', 'rotation']);
export type WebhookSourceHealthWriter = z.infer<typeof WebhookSourceHealthWriterSchema>;

/**
 * True when a landed health write MOVED the row: `status` or `last_error` differs between
 * the pre-image the compare-and-set replaced and what it wrote. The one gate both writers
 * and {@link WebhookSourceHealthChangedOutputSchema} share.
 */
export function webhookSourceHealthMoved(
  from: { readonly status: string; readonly lastError: string | null },
  to: { readonly status: string; readonly lastError: string | null },
): boolean {
  return from.status !== to.status || from.lastError !== to.lastError;
}

/**
 * One stored `status` / `last_error` as a {@link WebhookSourceHealthSnapshot}: a
 * `last_error` outside both closed sets becomes `unrecognized`, everything else is kept.
 */
function webhookSourceHealthSnapshotOf(stored: {
  readonly status: WebhookSourceHealthSnapshot['status'];
  readonly lastError: string | null;
}): WebhookSourceHealthSnapshot {
  if (stored.lastError === null) return { status: stored.status, lastError: null };
  const known = WebhookSourceKnownLastErrorSchema.safeParse(stored.lastError);
  return { status: stored.status, lastError: known.success ? known.data : 'unrecognized' };
}

/**
 * The ONE mapping every health writer (ingest's `trackSourceHealth`, the worker's
 * `recordReadHealth`, the api's signing-secret rotation, #6180) applies to a landed
 * write (#6170): `null` when the write moved nothing, else the `from` / `to` of its
 * `webhook_source.health_changed` row.
 *
 * "Moved" is decided on the STORED values, before mapping: `legacy_a` -> `legacy_b` is a
 * real transition even though both sides record as `unrecognized`. The mapping keeps
 * `status`, `null` and every closed-set value as they are, and sends only an unclosed
 * string to `unrecognized`; so a stored pair that moved maps to a pair the schema's
 * refine accepts, and the strict parse after it cannot leave a landed transition
 * unaudited. Pure: no clock, no IO.
 */
export function webhookSourceHealthTransition(
  from: {
    readonly status: WebhookSourceHealthSnapshot['status'];
    readonly lastError: string | null;
  },
  to: { readonly status: WebhookSourceHealthSnapshot['status']; readonly lastError: string | null },
): { from: WebhookSourceHealthSnapshot; to: WebhookSourceHealthSnapshot } | null {
  if (!webhookSourceHealthMoved(from, to)) return null;
  return { from: webhookSourceHealthSnapshotOf(from), to: webhookSourceHealthSnapshotOf(to) };
}

/**
 * `webhook_source.health_changed` (#6167) — one row per LANDED health transition of a
 * webhook source: the compare-and-set returned its pre-image (`from`, read off the very
 * row version it replaced, `RETURNING old.*`) and `from` differs from what it wrote
 * (`to`). `writer` names which writer moved it. A lost compare-and-set and
 * a no-op write move nothing and write no row; `from === to` is refused here too. Strict:
 * ids and the two closed sets only, never a payload, a message or a secret.
 *
 * The refine refuses only a no-op the payload can PROVE (#6170): two `unrecognized`
 * sides on one `status` may stand for two different stored values, a transition
 * {@link webhookSourceHealthTransition} decided on those values, so that pair passes.
 */
export const WebhookSourceHealthChangedOutputSchema = z
  .strictObject({
    sourceId: WebhookSourceIdSchema,
    from: WebhookSourceHealthSnapshotSchema,
    to: WebhookSourceHealthSnapshotSchema,
    writer: WebhookSourceHealthWriterSchema,
  })
  .refine(
    (v) =>
      webhookSourceHealthMoved(v.from, v.to) ||
      (v.from.lastError === 'unrecognized' && v.to.lastError === 'unrecognized'),
    {
      message: 'webhook_source.health_changed: from and to are the same health — not a transition',
      path: ['to'],
    },
  );
export type WebhookSourceHealthChangedOutput = z.infer<
  typeof WebhookSourceHealthChangedOutputSchema
>;

/**
 * `blocker.guidance_written` — one row per ATTEMPT the guidance sweep completed on one
 * blocker (#6679), written by `apps/worker/src/cron/blocker-guidance.ts` through the
 * account-anchored sink. The `blockers.guidance` column keeps the surviving steps; this
 * row keeps the attempt, including the ones that produced nothing, which is the half an
 * operator asking "why is this blocker stepless" needs.
 *
 * NAMES AND COUNTS, NEVER THE STEPS. A step is prose a person reads on the blocker, and
 * an audit row is not a second place to keep it. `strictObject`, so a writer cannot let
 * the text ride along by adding a field.
 *
 * `model` is NOT nullable, and that is the shape saying what a row means: the sweep
 * writes nothing at all when it could not reach a model (no usable pool key, a throw in
 * the resolve), because stamping an attempt there would spend the blocker's ONE attempt on
 * a call nobody made. So every row that exists names the model that was actually asked.
 *
 * The refine pins the one pairing a reader depends on: `steps` carries at least one step
 * and no reason, `none` carries a reason and no steps. A row claiming `none` with four
 * steps, or `steps` with zero, would make every count built on either field a guess.
 */
export const BlockerGuidanceWrittenOutputSchema = z
  .strictObject({
    outcome: z.enum(['steps', 'none']),
    /** How many steps landed on the row — the length of what the adjudicator kept. */
    steps: z.number().int().min(0),
    /** Why there were none (`BlockerGuidanceNoneReason`); null on the `steps` outcome. */
    reason: BlockerGuidanceNoneReasonSchema.nullable(),
    /** The model that was asked, as the picker spells it. */
    model: z.string().min(1),
    /**
     * WHICH PROMPT wrote them: the hash, the bytes and the layer names of the system
     * prompt this call sent (`promptAuditOf`), never its text. Without it "these steps
     * were useless" cannot be traced to the rules that produced them, and the rules are
     * the half of this feature that gets edited.
     */
    prompt: PromptAuditSchema,
  })
  .refine(
    (v) =>
      v.outcome === 'steps' ? v.steps > 0 && v.reason === null : v.steps === 0 && v.reason !== null,
    {
      message:
        'blocker.guidance_written: `steps` carries steps and no reason, `none` carries a reason and no steps',
      path: ['outcome'],
    },
  );
export type BlockerGuidanceWrittenOutput = z.infer<typeof BlockerGuidanceWrittenOutputSchema>;
